import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Divider,
  DialogContentText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoneyIcon from '@mui/icons-material/Money';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

const AdvancePaymentManagement = () => {
  const [advancePayments, setAdvancePayments] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedAdvance, setSelectedAdvance] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(1)), // Đầu tháng hiện tại
    endDate: new Date() // Ngày hiện tại
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    paid: 0,
    totalAmount: 0
  });
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteAdvanceId, setDeleteAdvanceId] = useState(null);
  
  // Fetch tất cả yêu cầu ứng tiền
  const fetchAdvancePayments = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/livestream/admin/advance-payments`, {
        params: {
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString(),
          status: statusFilter !== 'all' ? statusFilter : undefined
        }
      });
      
      if (response.data && response.data.success) {
        setAdvancePayments(response.data.data);
        
        // Tính toán thống kê
        const allAdvances = response.data.data;
        const stats = {
          total: allAdvances.length,
          pending: allAdvances.filter(item => item.status === 'pending').length,
          approved: allAdvances.filter(item => item.status === 'approved').length,
          rejected: allAdvances.filter(item => item.status === 'rejected').length,
          paid: allAdvances.filter(item => item.status === 'paid').length,
          totalAmount: allAdvances.reduce((sum, item) => sum + item.amount, 0)
        };
        setStats(stats);
      }
    } catch (error) {
      console.error('Error fetching advance payments:', error);
      showNotification('Không thể lấy danh sách yêu cầu ứng tiền', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, statusFilter]);
  
  useEffect(() => {
    fetchAdvancePayments();
  }, [fetchAdvancePayments]);
  
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleViewDetails = (advance) => {
    setSelectedAdvance(advance);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  const handleApprove = async () => {
    if (!selectedAdvance) return;
    
    try {
      const response = await axios.put(`${API_URL}/livestream/admin/advance-payments/${selectedAdvance._id}/approve`);
      
      if (response.data && response.data.success) {
        showNotification('Đã duyệt yêu cầu ứng tiền thành công');
        setOpenDialog(false);
        fetchAdvancePayments();
      }
    } catch (error) {
      console.error('Error approving advance payment:', error);
      showNotification('Không thể duyệt yêu cầu ứng tiền', 'error');
    }
  };
  
  const handleReject = async () => {
    if (!selectedAdvance) return;
    
    try {
      const response = await axios.put(`${API_URL}/livestream/admin/advance-payments/${selectedAdvance._id}/reject`);
      
      if (response.data && response.data.success) {
        showNotification('Đã từ chối yêu cầu ứng tiền');
        setOpenDialog(false);
        fetchAdvancePayments();
      }
    } catch (error) {
      console.error('Error rejecting advance payment:', error);
      showNotification('Không thể từ chối yêu cầu ứng tiền', 'error');
    }
  };
  
  const handleMarkAsPaid = async () => {
    if (!selectedAdvance) return;
    
    try {
      const response = await axios.put(`${API_URL}/livestream/admin/advance-payments/${selectedAdvance._id}/mark-paid`);
      
      if (response.data && response.data.success) {
        showNotification('Đã đánh dấu đã thanh toán thành công');
        setOpenDialog(false);
        fetchAdvancePayments();
      }
    } catch (error) {
      console.error('Error marking advance payment as paid:', error);
      showNotification('Không thể đánh dấu đã thanh toán', 'error');
    }
  };
  
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };
  
  const handleStartDateChange = (date) => {
    setDateRange(prev => ({ ...prev, startDate: date }));
  };
  
  const handleEndDateChange = (date) => {
    setDateRange(prev => ({ ...prev, endDate: date }));
  };
  
  const getStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip label="Đang chờ" color="warning" size="small" />;
      case 'approved':
        return <Chip label="Đã duyệt" color="info" size="small" />;
      case 'rejected':
        return <Chip label="Từ chối" color="error" size="small" />;
      case 'paid':
        return <Chip label="Đã thanh toán" color="success" size="small" />;
      default:
        return <Chip label="Không xác định" color="default" size="small" />;
    }
  };
  
  // Mở dialog xác nhận xóa
  const handleOpenDeleteDialog = (id) => {
    setDeleteAdvanceId(id);
    setOpenDeleteDialog(true);
  };
  
  // Đóng dialog xác nhận xóa
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteAdvanceId(null);
  };
  
  // Xóa yêu cầu ứng tiền
  const handleDelete = async () => {
    if (!deleteAdvanceId) return;
    
    try {
      const response = await axios.delete(`${API_URL}/livestream/admin/advance-payments/${deleteAdvanceId}`);
      
      if (response.data && response.data.success) {
        showNotification('Đã xóa yêu cầu ứng tiền thành công');
        handleCloseDeleteDialog();
        setOpenDialog(false); // Đóng dialog chi tiết nếu đang mở
        fetchAdvancePayments();
      }
    } catch (error) {
      console.error('Error deleting advance payment:', error);
      showNotification('Không thể xóa yêu cầu ứng tiền', 'error');
    }
  };
  
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
          Quản lý ứng tiền
        </Typography>
        
        {/* Thống kê */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Tổng yêu cầu</Typography>
                <Typography variant="h4" sx={{ mt: 1, color: '#1a237e' }}>{stats.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 2, borderLeft: '4px solid #ff9800' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Đang chờ duyệt</Typography>
                <Typography variant="h4" sx={{ mt: 1, color: '#ff9800' }}>{stats.pending}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 2, borderLeft: '4px solid #4caf50' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Đã thanh toán</Typography>
                <Typography variant="h4" sx={{ mt: 1, color: '#4caf50' }}>{stats.paid}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', borderRadius: 2, borderLeft: '4px solid #1976d2' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Tổng tiền ứng</Typography>
                <Typography variant="h4" sx={{ mt: 1, color: '#1976d2' }}>
                  {new Intl.NumberFormat('vi-VN', {
                    style: 'currency',
                    currency: 'VND'
                  }).format(stats.totalAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Bộ lọc */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Trạng thái"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                variant="outlined"
              >
                <MenuItem value="all">Tất cả</MenuItem>
                <MenuItem value="pending">Đang chờ</MenuItem>
                <MenuItem value="approved">Đã duyệt</MenuItem>
                <MenuItem value="rejected">Từ chối</MenuItem>
                <MenuItem value="paid">Đã thanh toán</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Từ ngày"
                value={dateRange.startDate}
                onChange={handleStartDateChange}
                slotProps={{ 
                  textField: { variant: 'outlined', fullWidth: true } 
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <DatePicker
                label="Đến ngày"
                value={dateRange.endDate}
                onChange={handleEndDateChange}
                slotProps={{ 
                  textField: { variant: 'outlined', fullWidth: true } 
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={fetchAdvancePayments}
                fullWidth
                sx={{ 
                  py: 1.5, 
                  color: 'white',
                  bgcolor: '#1a237e',
                  '&:hover': { bgcolor: '#534bae' }
                }}
              >
                Cập nhật
              </Button>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Bảng dữ liệu */}
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell>Người dùng</TableCell>
                  <TableCell>Số tiền</TableCell>
                  <TableCell>Ngày yêu cầu</TableCell>
                  <TableCell>Kỳ thanh toán</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {advancePayments
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((advance) => (
                    <TableRow key={advance._id} hover>
                      <TableCell>
                        {advance.userId && typeof advance.userId === 'object' 
                          ? advance.userId.fullName 
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        {new Intl.NumberFormat('vi-VN', {
                          style: 'currency',
                          currency: 'VND'
                        }).format(advance.amount)}
                      </TableCell>
                      <TableCell>
                        {new Date(advance.requestDate).toLocaleDateString('vi-VN')}
                      </TableCell>
                      <TableCell>
                        {advance.periodId && typeof advance.periodId === 'object' 
                          ? `${new Date(advance.periodId.periodStart).toLocaleDateString('vi-VN')} - ${new Date(advance.periodId.periodEnd).toLocaleDateString('vi-VN')}` 
                          : 'N/A'}
                      </TableCell>
                      <TableCell>{getStatusChip(advance.status)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(advance)}
                          >
                            Chi tiết
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(advance._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                {advancePayments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography sx={{ py: 2 }}>
                        Không có yêu cầu ứng tiền nào
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={advancePayments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Số hàng mỗi trang:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
          />
        </Paper>
        
        {/* Dialog chi tiết */}
        {selectedAdvance && (
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white' }}>
              Chi tiết yêu cầu ứng tiền
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Người yêu cầu:</strong> {selectedAdvance.userId && typeof selectedAdvance.userId === 'object' 
                      ? selectedAdvance.userId.fullName 
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Số tiền:</strong> {new Intl.NumberFormat('vi-VN', {
                      style: 'currency',
                      currency: 'VND'
                    }).format(selectedAdvance.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Ngày yêu cầu:</strong> {new Date(selectedAdvance.requestDate).toLocaleString('vi-VN')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Kỳ thanh toán:</strong> {selectedAdvance.periodId && typeof selectedAdvance.periodId === 'object' 
                      ? `${new Date(selectedAdvance.periodId.periodStart).toLocaleDateString('vi-VN')} - ${new Date(selectedAdvance.periodId.periodEnd).toLocaleDateString('vi-VN')}` 
                      : 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Trạng thái:</strong> {getStatusChip(selectedAdvance.status)}
                  </Typography>
                </Grid>
                {selectedAdvance.approvedDate && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      <strong>Ngày duyệt:</strong> {new Date(selectedAdvance.approvedDate).toLocaleString('vi-VN')}
                    </Typography>
                  </Grid>
                )}
                {selectedAdvance.paidDate && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">
                      <strong>Ngày thanh toán:</strong> {new Date(selectedAdvance.paidDate).toLocaleString('vi-VN')}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle1">
                    <strong>Ghi chú:</strong> {selectedAdvance.notes || 'Không có'}
                  </Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              {selectedAdvance.status === 'pending' && (
                <>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={handleReject}
                    startIcon={<BlockIcon />}
                  >
                    Từ chối
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleApprove}
                    startIcon={<CheckCircleIcon />}
                    sx={{ 
                      bgcolor: '#1a237e',
                      '&:hover': { bgcolor: '#534bae' }
                    }}
                  >
                    Duyệt
                  </Button>
                </>
              )}
              {selectedAdvance.status === 'approved' && (
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handleMarkAsPaid}
                  startIcon={<MoneyIcon />}
                >
                  Đánh dấu đã thanh toán
                </Button>
              )}
              <Button 
                variant="outlined" 
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleOpenDeleteDialog(selectedAdvance._id)}
                sx={{ mr: 'auto' }}
              >
                Xóa
              </Button>
              <Button onClick={handleCloseDialog}>Đóng</Button>
            </DialogActions>
          </Dialog>
        )}
        
        {/* Dialog xác nhận xóa */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            Xác nhận xóa
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bạn có chắc chắn muốn xóa yêu cầu ứng tiền này? Hành động này không thể hoàn tác.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDeleteDialog}>
              Hủy
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={handleDelete}
              startIcon={<DeleteIcon />}
            >
              Xóa
            </Button>
          </DialogActions>
        </Dialog>
        
        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default AdvancePaymentManagement; 